import { useState, useEffect, useRef } from 'react';
import decommissionAnimationDetector from './decommissionAnimationDetector';

const {
  collectDecommissionAnimationTriggerKeys,
  hasNewDecommissionAnimationTrigger,
  isDecommissionedUnitInHand
} = decommissionAnimationDetector;

/**
 * Hook to detect units decommissioned (Kinetron Blast 111, Siege Tank 82, HTS 354, etc.)
 * Tracks units that disappear from battlefield and appear in hand (decommissioned)
 * Returns map of decommissioned units for animation
 */
export const useDecommissionAnimations = (gameState, userId) => {
  const [decommissionedUnits, setDecommissionedUnits] = useState(new Map());
  const previousUnitsRef = useRef(new Map());
  const animationTimeoutRef = useRef(new Map());
  const activeDecommissionKeysRef = useRef(new Set());
  const previousDecommissionTriggerKeysRef = useRef(new Set());

  useEffect(() => {
    if (!gameState?.state || !userId) return;

    const allPlayerIds = gameState.players || Object.keys(gameState.state || {});
    const currentUnits = new Map();

    // Build map of current units on battlefield
    allPlayerIds.forEach(playerId => {
      const player = gameState.state[playerId];
      if (!player?.playedOrders) return;

      ['front', 'flank'].forEach(zone => {
        if (player.playedOrders[zone]) {
          player.playedOrders[zone].forEach((unit, idx) => {
            if (unit && unit.instanceId) {
              const key = `${playerId}_${unit.instanceId}`;
              // Store full unit data for decommission detection
              currentUnits.set(key, {
                instanceId: unit.instanceId,
                id: unit.id,
                name: unit.name,
                zone,
                index: idx,
                playerId,
                // Store full unit object for animation
                fullUnit: { ...unit }
              });
            }
          });
        }
      });
    });

    // Detect if a NEW decommission source fired this tick (strategem 111 completed,
    // deploy decommission providedOrders for 82/354, etc.) so animations do not replay later.
    const currentDecommissionTriggerKeys = collectDecommissionAnimationTriggerKeys(gameState);
    const hasNewDecommissionTrigger = hasNewDecommissionAnimationTrigger(
      previousDecommissionTriggerKeysRef.current,
      currentDecommissionTriggerKeys
    );

    // Detect decommissioned units (unit was on battlefield, now in hand)
    const newDecommissions = new Map();
    if (hasNewDecommissionTrigger) {
      previousUnitsRef.current.forEach((previousUnit, key) => {
        // Prevent duplicate overlays/timeouts for the same removed instance
        if (activeDecommissionKeysRef.current.has(key)) {
          return;
        }

        // Check if unit is no longer on battlefield
        if (!currentUnits.has(key)) {
          const unitOwner = gameState.state?.[previousUnit.playerId];
          if (unitOwner?.hand && isDecommissionedUnitInHand(unitOwner.hand, previousUnit)) {
            const zonePrefix = String(previousUnit.playerId) === String(userId) ? 'your' : 'opponent';
            const zoneName = `${zonePrefix}_${previousUnit.zone}`;

            const fullUnitData = previousUnit.fullUnit || {
              id: previousUnit.id,
              name: previousUnit.name,
              instanceId: previousUnit.instanceId
            };

            newDecommissions.set(key, {
              instanceId: previousUnit.instanceId,
              id: previousUnit.id,
              name: previousUnit.name,
              zone: zoneName,
              index: previousUnit.index,
              playerId: previousUnit.playerId,
              isPlayerUnit: String(previousUnit.playerId) === String(userId),
              unitData: fullUnitData
            });

            activeDecommissionKeysRef.current.add(key);

            const timeoutId = setTimeout(() => {
              setDecommissionedUnits(prev => {
                const next = new Map(prev);
                next.delete(key);
                return next;
              });
              animationTimeoutRef.current.delete(key);
              activeDecommissionKeysRef.current.delete(key);
            }, 2000);

            animationTimeoutRef.current.set(key, timeoutId);
          }
        }
      });
    }

    // Update decommissioned units
    if (newDecommissions.size > 0) {
      setDecommissionedUnits(prev => {
        const next = new Map(prev);
        newDecommissions.forEach((decommission, key) => {
          next.set(key, decommission);
        });
        return next;
      });
    }

    // Update previous units reference
    previousUnitsRef.current = currentUnits;
    previousDecommissionTriggerKeysRef.current = currentDecommissionTriggerKeys;
  }, [gameState, userId]);

  // Cleanup only on unmount, not on every gameState tick.
  useEffect(() => {
    return () => {
      animationTimeoutRef.current.forEach(timeoutId => clearTimeout(timeoutId));
      animationTimeoutRef.current.clear();
      activeDecommissionKeysRef.current.clear();
      previousDecommissionTriggerKeysRef.current.clear();
    };
  }, []);

  return {
    decommissionedUnits
  };
};
